import {
  QuoteResponseLineItem,
  QuoteResponse,
  QuoteRequest,
  PricingTier,
  PagedQuoteHistory,
  QuoteDetailItem,
  ZendeskUsers,
  GenerateSlidesPayload,
} from 'lib/Model/QuoteGenerator';
import pricingData from './data/pricing.json';
import pricingDataOld from './data/pricing_old.json';
import { Config } from 'config/Config';
import { getAccessToken } from 'lib/Helper/Auth';
import axios from 'axios';

export function determineTierSize(
  companySize: number,
  tiers: any = pricingData.tiers,
): string {
  if (
    companySize >= tiers.tier_1.minEmployees &&
    companySize <= tiers.tier_1.maxEmployees
  )
    return 'tier_1';
  if (
    companySize >= tiers.tier_2.minEmployees &&
    companySize <= tiers.tier_2.maxEmployees
  )
    return 'tier_2';
  if (
    companySize >= tiers.tier_3.minEmployees &&
    companySize <= tiers.tier_3.maxEmployees
  )
    return 'tier_3';
  if (
    companySize >= tiers.tier_4.minEmployees &&
    companySize <= tiers.tier_4.maxEmployees
  )
    return 'tier_4';
  return 'Unknown';
}

export function generateQuote(request: QuoteRequest): QuoteResponse {
  const currentPricingData = request.useOldPricing
    ? pricingDataOld
    : pricingData;
  const effectiveCompanySize = Math.max(request.companySize, 10);
  const tierKey = determineTierSize(
    effectiveCompanySize,
    currentPricingData.tiers,
  ) as PricingTier;

  const CORE_HARDWARE_KEYS = new Set([
    'uA760',
    'silkBio',
    'speedface',
    'horus',
    'shared_team_virtual_punchclock',
    'rfid_em_cards',
  ]);

  const MIN_QTY_MODULES = new Set([
    'hr_essentials_leave',
    'payroll',
    'performance',
    'training_management',
  ]);

  let modulesItems: QuoteResponseLineItem[] = [];
  let hardwareItems: QuoteResponseLineItem[] = [];
  let extrasItems: QuoteResponseLineItem[] = [];
  let monthlyTotal = 0;
  let setupTotal = 0;
  let unitPriceTotal = 0;
  let atsUnitPriceTotal = 0;

  for (const module of request.modules) {
    const isHardware = CORE_HARDWARE_KEYS.has(module.moduleKey);
    const isATS =
      module.moduleKey === 'admin_licence' ||
      module.moduleKey === 'line_manager_licence';
    const isCspPayroll =
      !!request.isCspProposal && module.moduleKey === 'payroll';
    const isMinQtyModule =
      MIN_QTY_MODULES.has(module.moduleKey) && !isCspPayroll;

    const displayQty =
      module.userQty !== undefined
        ? module.userQty
        : isHardware
        ? 1
        : module.moduleKey === 'csp_per_payslip'
        ? 0
        : effectiveCompanySize;

    const modulePricing = currentPricingData.pricing[tierKey];
    const addonLookupKey = isCspPayroll ? 'csp_per_payslip' : module.moduleKey;
    const addon = modulePricing.addons.find((a) => a.module === addonLookupKey);

    if (addon) {
      if (isHardware) {
        const discountUnitPrice = module.discountUnitPrice ?? 0;
        const isVirtualPunchclock =
          module.moduleKey === 'shared_team_virtual_punchclock';
        const isSelfInstallDevice =
          (module.moduleKey === 'uA760' ||
            module.moduleKey === 'silkBio' ||
            module.moduleKey === 'speedface' ||
            module.moduleKey === 'horus') &&
          request.selfInstall;
        const originalSetupFee = addon.setup_price ?? 0;
        const discountSetupFee = isSelfInstallDevice
          ? -originalSetupFee
          : module.discountSetupFee ?? 0;
        const effectiveSetupFee = Math.max(
          originalSetupFee + discountSetupFee,
          0,
        );

        if (isVirtualPunchclock) {
          const originalMonthlyPrice = addon.price ?? 0;
          const effectiveMonthlyPrice = Math.max(
            originalMonthlyPrice + discountUnitPrice,
            0,
          );
          const monthlyPrice = effectiveMonthlyPrice * displayQty;
          const setupFee = effectiveSetupFee * displayQty;

          hardwareItems.push({
            moduleKey: module.moduleKey,
            userQty: displayQty,
            price: monthlyPrice,
            originalPrice: originalMonthlyPrice,
            discountUnitPrice: discountUnitPrice,
            itemType: 'hardware',
            monthlyUnitPrice: effectiveMonthlyPrice,
            setupFee: setupFee,
            originalMonthlyUnitPrice: originalMonthlyPrice,
            discountMonthlyUnitPrice: discountUnitPrice,
            originalSetupFee: originalSetupFee,
            discountSetupFee,
          });
          monthlyTotal += monthlyPrice;
          setupTotal += setupFee;
          unitPriceTotal += effectiveMonthlyPrice;
        } else {
          const hardwarePrice = addon.clocking_pro_price ?? addon.price ?? 0;
          const effectiveHardwarePrice = Math.max(
            hardwarePrice + discountUnitPrice,
            0,
          );
          const cost = effectiveHardwarePrice * displayQty;
          const setupFee = effectiveSetupFee * displayQty;

          hardwareItems.push({
            moduleKey: module.moduleKey,
            userQty: displayQty,
            price: cost,
            originalPrice: hardwarePrice,
            discountUnitPrice: discountUnitPrice,
            itemType: 'hardware',
            oneTimeUnitPrice: effectiveHardwarePrice,
            setupFee: setupFee,
            originalOneTimeUnitPrice: hardwarePrice,
            discountOneTimeUnitPrice: discountUnitPrice,
            originalSetupFee: originalSetupFee,
            discountSetupFee,
          });
          setupTotal += cost + setupFee;
        }
      } else {
        const discountUnitPrice = module.discountUnitPrice ?? 0;
        const discountSetupFee = module.discountSetupFee ?? 0;
        const originalPrice = addon.price ?? 0;
        const originalSetupFee = addon.setup_price ?? 0;
        const effectivePrice = Math.max(originalPrice + discountUnitPrice, 0);
        const effectiveSetupPrice = Math.max(
          originalSetupFee + discountSetupFee,
          0,
        );

        const pricingQty = isMinQtyModule && displayQty < 10 ? 10 : displayQty;

        const modulePrice = effectivePrice * pricingQty;

        modulesItems.push({
          moduleKey: module.moduleKey,
          userQty: displayQty,
          price: modulePrice,
          originalPrice,
          discountUnitPrice,
          originalSetupFee,
          discountSetupFee,
          itemType: 'module',
          monthlyUnitPrice: effectivePrice,
          setupFee: effectiveSetupPrice,
          originalMonthlyUnitPrice: originalPrice,
          discountMonthlyUnitPrice: discountUnitPrice,
        });
        monthlyTotal += modulePrice;
        setupTotal += effectiveSetupPrice;

        if (isATS) {
          atsUnitPriceTotal += effectivePrice;
        } else {
          unitPriceTotal += effectivePrice;
        }
      }
    }

    const extra = currentPricingData.extras.find(
      (e) => e.key === module.moduleKey,
    );
    if (extra) {
      const originalPrice = extra.price ?? 0;
      const originalSetupFee = extra.setup_price ?? 0;

      if (
        (module.moduleKey === 'signrequest' ||
          module.moduleKey === 'reporting_database' ||
          module.moduleKey === 'visitor_management') &&
        module.userQty
      ) {
        const discountUnitPrice = module.discountUnitPrice ?? 0;
        const effectivePrice = Math.max(originalPrice + discountUnitPrice, 0);
        const price = effectivePrice * module.userQty;
        extrasItems.push({
          moduleKey: module.moduleKey,
          userQty: module.userQty,
          price,
          originalPrice,
          discountUnitPrice,
          itemType: 'extra',
          monthlyUnitPrice: effectivePrice,
          originalMonthlyUnitPrice: originalPrice,
          discountMonthlyUnitPrice: discountUnitPrice,
          originalSetupFee: 0,
          discountSetupFee: 0,
          setupFee: 0,
        });
        monthlyTotal += price;
        unitPriceTotal += effectivePrice;
      } else if (
        (module.moduleKey === 'parallel_payroll_runs' ||
          module.moduleKey === 'data_migration' ||
          module.moduleKey === 'onboarding_session') &&
        module.userQty
      ) {
        const discountSetupFee = module.discountSetupFee ?? 0;
        const effectiveSetupPrice = Math.max(
          originalSetupFee + discountSetupFee,
          0,
        );
        const setupPrice = effectiveSetupPrice * module.userQty;
        extrasItems.push({
          moduleKey: module.moduleKey,
          userQty: module.userQty,
          price: 0,
          originalSetupFee,
          discountSetupFee,
          itemType: 'extra',
          oneTimeUnitPrice: effectiveSetupPrice,
          originalOneTimeUnitPrice: originalSetupFee,
          discountOneTimeUnitPrice: discountSetupFee,
          setupFee: setupPrice,
          originalPrice: 0,
          discountUnitPrice: 0,
        });
        setupTotal += setupPrice;
      }
    }
  }

  return {
    companyName: request.companyName,
    companyId: request.companyId,
    numberOfCompanies: request.numberOfCompanies || 1,
    requestedBy: request.requestedBy,
    modules: modulesItems,
    hardware: hardwareItems,
    extras: extrasItems,
    monthlyFee: monthlyTotal,
    annualFee: monthlyTotal * 12,
    oneTimeFee: setupTotal,
    totalUnitPrice: unitPriceTotal,
    atsUnitPrice: atsUnitPriceTotal,
    isCspProposal: !!request.isCspProposal,
  };
}

export async function saveQuote(
  quote: QuoteResponse,
): Promise<{ message: string; quote_id: number }> {
  const response = await axios.post(
    `${Config.API_BASE_URL}intranet/quote_generator/save`,
    quote,
    {
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );

  return response.data;
}

export async function fetchQuoteHistory(
  pageNumber: number,
  itemsPerPage: number,
  companyName?: string,
  companyId?: string,
): Promise<PagedQuoteHistory> {
  const response = await axios.get(
    `${Config.API_BASE_URL}intranet/quote_generator/quotes`,
    {
      params: {
        page: pageNumber,
        page_size: itemsPerPage,
        search_param: companyName,
        company_id: companyId,
      },
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );

  return response.data;
}

export async function fetchQuoteDetails(
  quoteId: number,
): Promise<QuoteDetailItem[]> {
  const response = await axios.get(
    `${Config.API_BASE_URL}intranet/quote_generator/quotes/${quoteId}/details`,
    {
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );

  return response.data.data;
}

export async function fetchCompanyQuotes(
  companyId: string,
  pageNumber: number = 1,
  itemsPerPage: number = 10,
): Promise<PagedQuoteHistory> {
  const response = await axios.get(
    `${Config.API_BASE_URL}intranet/companies/${companyId}/quotes`,
    {
      params: {
        page: pageNumber,
        page_size: itemsPerPage,
      },
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );

  return response.data;
}

export async function updateQuoteCompany(
  quoteId: number,
  companyId: string,
  companyName: string,
): Promise<{ message: string; quote_id: number }> {
  const response = await axios.put(
    `${Config.API_BASE_URL}intranet/quote_generator/quotes/${quoteId}`,
    {
      companyId,
      companyName,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );

  return response.data;
}

export async function generateQuoteSlides(
  quoteId: number,
  request: GenerateSlidesPayload,
): Promise<{
  document_id: string;
  download_link?: string;
  deal_id?: number;
  quote_id: number;
}> {
  const apiResponse = await axios.post(
    `${Config.API_BASE_URL}intranet/quote_generator/quotes/${quoteId}/generate`,
    request,
    {
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );

  return apiResponse.data;
}

export async function fetchZendeskUsers(): Promise<ZendeskUsers[]> {
  const r = await axios.get(
    `${Config.API_BASE_URL}intranet/quote_generator/zendesk_users`,
    {
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );

  return r.data.data;
}

export async function checkQuoteFolder(
  quoteId: number,
): Promise<{ exists: boolean; quote_id: number; folder_id?: string }> {
  const response = await axios.get(
    `${Config.API_BASE_URL}intranet/quote_generator/quotes/${quoteId}/check_folder`,
    {
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );

  return response.data;
}
